import { Close, InfoOutlined } from "@mui/icons-material";
import type React from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import type { Size, AppContext, Store } from "../types";
import { useNavigate } from "react-router-dom";
import { scroller } from "react-scroll";
import { Axios, getSid } from "../common/utils";
import { baseApi } from "../common/constants";
import { ClickableText } from "./account/components/Typography";
import { LiLink, LiTab } from "../common/commonComps";
import { styled, Box, Button, Tabs, List, ListItem, ListItemText, useTheme } from "@mui/material";

const PageFrame = styled(Box)<{ size: Size; open: boolean }>(({ size, open }) => ({
    padding: size === `small` ? `20px` : `30px`,
    position: `fixed`,
    right: open ? 0 : size === `small` ? `-100%` : `-500px`,
    zIndex: 10000,
    width: size === `small` ? `100%` : `500px`,
    height: `100%`,
    transition: `all 0.3s ease 0s`,
    top: `0px`,
    background: `#FFFFFF`,
    "& div, button": {
        userSelect: `none`,
    },

    overflow: `auto`,
}));

const TopRow = styled(Box)(() => ({
    display: `flex`,
    justifyContent: `space-between`,
    alignItems: `center`,
}));

const BoxFrame = styled(Box)(() => ({
    maxWidth: `700px`,
    border: `1px solid #dcdee1`,
    borderRadius: `5px`,
    display: `flex`,
    flexDirection: `column`,
    alignItems: `center`,
    marginTop: 20,
}));

const TextLine = styled(Box)(() => ({
    fontSize: 16,
    fontWeight: 400,
    color: `#333`,
}));

const ContentLine = styled(Box)(() => ({
    minHeight: `40px`,
    marginBottom: `15px`,
}));

const ShadedCircle = styled(Box)(() => ({
    display: `flex`,
    alignItems: `center !important`,
    justifyContent: `center `,
    background: `#eee`,
    borderRadius: `50%`,
    cursor: `pointer`,
    width: `35px`,
    height: `35px`,
    fontWeight: `500`,
}));

const CCRow = styled(Box)(() => ({
    minHeight: `30px`,
    display: `table-row`,
    marginBottom: 1,
}));

const CCLeft = styled(Box)(() => ({
    padding: `0 10px 0 0`,
    display: `table-cell`,
    verticalAlign: `top`,
}));

const CCRight = styled(Box)(() => ({
    display: `table-cell`,
    verticalAlign: `top`,
    borderLeft: `1px solid #ccc`,
    padding: `0 0 10px 10px`,
}));

const ShopButton = styled(Button)(() => ({
    margin: `40px auto`,
    fontSize: 18,
    fontWeight: 500,
    cursor: `pointer`,
    backgroundColor: `#ce392b !important`,
    color: `#fff`,
    borderRadius: `50px`,
    padding: `9px 50px !important`,
}));

const CustomTabPanel = ({
    children,
    value,
    index,
    ...other
}: { children: React.ReactNode; value: number; index: number } & React.HTMLAttributes<HTMLDivElement>) => (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} {...other}>
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
);

const StoreSlideOver = ({
    store,
    setStore,
    size,
    context,
}: {
    store: Store | null;
    setStore: (v: Store | null) => void;
    size: Size;
    context: AppContext;
}) => {
    const [tab, st] = useState<number>(0);
    const [storeDetails, setStoreDetails] = useState<{
        exclusions: string;
        commissionCategories: {
            commCategoryName: string;
            commCategoryAmount: number;
            commCategoryIsDollars: boolean;
        }[];
    } | null>(null);
    const [showTerms, setShowTerms] = useState(false);
    const [showLearnMore, setShowLearnMore] = useState(false);
    const navigate = useNavigate();
    const setTab = (e: React.SyntheticEvent, v: number): void => {
        st(v);
        setShowTerms(false);
        setShowLearnMore(false);
    };
    const theme = useTheme();

    useEffect(() => {
        setShowTerms(false);
        setShowLearnMore(false);
        st(0);
        if (store) {
            Axios.get(`${baseApi}getstore?a=${store.aid}&s=${store.asid}`)
                .then(
                    (r: {
                        data: {
                            commissionCategories?: {
                                commCategoryName: string;
                                commCategoryAmount: number;
                                commCategoryIsDollars: boolean;
                            }[];
                            exclusions?: string;
                        };
                    }) => {
                        const { commissionCategories = [], exclusions = `` } = r.data;
                        const cc: {
                            commCategoryName: string[];
                            commCategoryAmount: number;
                            commCategoryIsDollars: boolean;
                        }[] = [];
                        commissionCategories.forEach(c => {
                            const ccToUp = cc.find(
                                cx =>
                                    cx.commCategoryAmount === c.commCategoryAmount &&
                                    cx.commCategoryIsDollars === c.commCategoryIsDollars
                            );
                            if (!ccToUp) {
                                cc.push({
                                    commCategoryName: [c.commCategoryName],
                                    commCategoryAmount: c.commCategoryAmount,
                                    commCategoryIsDollars: c.commCategoryIsDollars,
                                });
                            } else {
                                ccToUp.commCategoryName.push(c.commCategoryName);
                            }
                        });
                        cc.sort((a, b) => (a.commCategoryAmount < b.commCategoryAmount ? 1 : -1));
                        setStoreDetails({
                            exclusions,
                            commissionCategories: cc.map(c => ({
                                ...c,
                                commCategoryName: c.commCategoryName.join(`, `),
                            })),
                        });
                    }
                )
                .catch(() => {
                    toast.error(`Sorry, something went wrong`);
                });
        }
    }, [store]);

    return (
        <PageFrame size={size} open={!!store}>
            <TopRow>
                <Box
                    sx={{ height: `auto`, width: `125px` }}
                    component="img"
                    src={context?.adminConfig?.mall?.Color_Desktop_Logo}
                />
                <ShadedCircle onClick={() => setStore(null)}>
                    <Close sx={{ opacity: 0.55 }} />
                </ShadedCircle>
            </TopRow>
            <BoxFrame>
                {store && (
                    <Box
                        component="img"
                        sx={{ padding: `40px 0 20px`, maxWidth: 225, maxHeight: 120, height: `auto`, width: `auto` }}
                        src={`https://s3.amazonaws.com/retailerimages/logos/${store?.aid}/${store?.asid}`}
                    />
                )}
                <TextLine sx={{ fontSize: 22, fontWeight: 500 }}>{store?.cbp}</TextLine>
                <LiLink
                    target="_blank"
                    to={`${baseApi}tooffer?aid=${store?.aid}&asid=${store?.asid}&auth=${encodeURIComponent(getSid() ?? ``)}`}
                >
                    <ShopButton variant="contained">Shop Now</ShopButton>
                </LiLink>
            </BoxFrame>
            <BoxFrame sx={{ alignItems: `flex-start` }}>
                <Box sx={{ borderBottom: 1, borderColor: `divider`, width: `100%` }}>
                    <Tabs
                        sx={{ "& .MuiTabs-indicator": { backgroundColor: theme.palette.account.accent } }}
                        value={tab}
                        onChange={setTab}
                    >
                        <LiTab label="HOW IT WORKS" />
                        <LiTab label="CATEGORIES" />
                        <LiTab label="TERMS" />
                    </Tabs>
                </Box>
                <Box sx={{ width: `100%` }}>
                    <CustomTabPanel value={tab} index={0}>
                        {showLearnMore && (
                            <Box sx={{ position: `relative`, padding: 0 }}>
                                <Close
                                    sx={{
                                        position: `absolute`,
                                        top: 0,
                                        right: 0,
                                        zIndex: 1,
                                        cursor: `pointer`,
                                        opacity: 0.55,
                                    }}
                                    onClick={() => setShowLearnMore(false)}
                                />
                                <List sx={{ width: `100%` }}>
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={
                                                <span>
                                                    <p>
                                                        <b>
                                                            <InfoOutlined
                                                                sx={{ verticalAlign: `middle`, marginRight: `10px` }}
                                                            />
                                                            Learn More About Cash Back
                                                        </b>
                                                    </p>
                                                    <p>
                                                        When you shop online, you can earn cash back. Your cash back
                                                        will be sent to you via PayPal or Venmo.
                                                    </p>
                                                    <p>
                                                        Click{` `}
                                                        <ClickableText
                                                            onClick={() => {
                                                                setStore(null);
                                                                scroller.scrollTo(`top`, {});
                                                                navigate(`/account/shopping`);
                                                            }}
                                                            sx={{
                                                                fontSize: 16,
                                                                padding: `0 5px 0 2px`,
                                                                fontWeight: 400,
                                                                minWidth: `unset`,
                                                                verticalAlign: `baseline`,
                                                            }}
                                                        >
                                                            here
                                                        </ClickableText>
                                                        to view your balance, see your shopping history, or adjust your
                                                        settings.
                                                    </p>
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        )}
                        {!showLearnMore && (
                            <>
                                <ContentLine>
                                    <ShadedCircle sx={{ position: `absolute` }}>1</ShadedCircle>
                                    <TextLine sx={{ marginLeft: `50px`, float: `left`, marginTop: `5px` }}>
                                        Click &quot;Shop Now&quot; above
                                    </TextLine>
                                </ContentLine>
                                <ContentLine>
                                    <ShadedCircle sx={{ position: `absolute` }}>2</ShadedCircle>
                                    <TextLine sx={{ marginLeft: `50px`, float: `left`, marginTop: `5px` }}>
                                        Complete a purchase online
                                    </TextLine>
                                </ContentLine>
                                <ContentLine>
                                    <ShadedCircle sx={{ position: `absolute` }}>3</ShadedCircle>
                                    <TextLine sx={{ marginLeft: `50px`, marginTop: `5px`, display: `inline-block` }}>
                                        Your cash back will be automatically added to your balance.{` `}
                                        <ClickableText onClick={() => setShowLearnMore(true)}>Learn More</ClickableText>
                                    </TextLine>
                                </ContentLine>
                            </>
                        )}
                    </CustomTabPanel>
                    <CustomTabPanel value={tab} index={1}>
                        {storeDetails?.commissionCategories.map((cc, ix) => (
                            <CCRow key={ix}>
                                <CCLeft>{`${cc.commCategoryIsDollars ? `$` : ``}${cc.commCategoryAmount}${
                                    cc.commCategoryIsDollars ? `` : `%`
                                }`}</CCLeft>
                                <CCRight>{cc.commCategoryName}</CCRight>
                            </CCRow>
                        ))}
                        {!storeDetails?.commissionCategories.length && (
                            <CCRow>
                                <CCLeft>{(store?.cbp ?? ``).replace(`Cash Back`, ``).replace(`Up To`, ``)}</CCLeft>
                                <CCRight>All</CCRight>
                            </CCRow>
                        )}
                    </CustomTabPanel>
                    <CustomTabPanel value={tab} index={2}>
                        {!showTerms && (
                            <List sx={{ listStyleType: `disc`, padding: `0 24px` }}>
                                <ListItem sx={{ display: `list-item`, padding: 0 }}>
                                    <ListItemText
                                        primary={
                                            <Box component="span">
                                                <Box component="span">
                                                    Excludes purchases made through the {store?.name} app
                                                </Box>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                <ListItem sx={{ display: `list-item`, padding: 0 }}>
                                    <ListItemText
                                        primary={
                                            <Box component="span">
                                                <Box component="span">Excludes purchase of gift cards</Box>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                {storeDetails?.exclusions && (
                                    <ListItem sx={{ display: `list-item`, padding: 0 }}>
                                        <ListItemText
                                            primary={
                                                <Box component="span">
                                                    <Box component="span">
                                                        Other terms and exclusions apply, see&nbsp;
                                                        <ClickableText onClick={() => setShowTerms(true)}>
                                                            Terms and Conditions
                                                        </ClickableText>
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                )}
                            </List>
                        )}
                        {showTerms && (
                            <Box sx={{ position: `relative`, padding: 0 }}>
                                <Close
                                    sx={{
                                        position: `absolute`,
                                        top: 0,
                                        right: 0,
                                        zIndex: 1,
                                        cursor: `pointer`,
                                        opacity: 0.55,
                                    }}
                                    onClick={() => setShowTerms(false)}
                                />
                                <List sx={{ width: `100%` }}>
                                    <ListItem disableGutters>
                                        <ListItemText primary={`Exclusions: ${storeDetails!.exclusions}`} />
                                    </ListItem>
                                </List>
                            </Box>
                        )}
                    </CustomTabPanel>
                </Box>
            </BoxFrame>
        </PageFrame>
    );
};

export default StoreSlideOver;
